'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'

export default function Post21Content() {
  return (
    <BlogPostLayout
      title="Microplastics in the Brain: What the 2026 Studies Reveal"
      author="MicroPlastics Team"
      date="March 15, 2026"
      readTime="10 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="MicroPlastics app tracking neurological exposure data"
    >
      <BlogTLDR>
        A 2025 University of New Mexico study found{' '}
        <strong>microplastics in human brain tissue increased ~50% between
        2016 and 2024</strong>, with patients diagnosed with dementia showing
        roughly <strong>10× higher</strong> microplastic concentrations than
        healthy controls. Two 2026 papers extended this: an April 2026{' '}
        <em>Nature Health</em> study confirmed the finding in{' '}
        <strong>living</strong> patients (100% of healthy brain samples, median
        50.3 µg/g), and a May 2026 <em>Brain Health</em> Perspective quantified
        the brain as holding <strong>7--30× more</strong> plastic than liver or
        kidney. None of this proves causation. Reduction strategies still focus
        on inhaled and ingested sources.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Microplastics in human brains rose ~50% from 2016 to 2024 (Nihart et al., UNM, 2025).</>,
          <>Dementia patients showed ~10× higher brain microplastic load than non-dementia controls.</>,
          <>2026 update: microplastics were found in 100% of healthy brain samples from <strong>living</strong> patients, median 50.3 µg/g, peaking at 129 µg/g in tumor tissue (Li et al., <em>Nature Health</em>, April 2026).</>,
          <>2026 update: the brain carries 7--30× the concentration found in liver or kidney (Licinio et al., <em>Brain Health</em>, May 2026).</>,
          <>Therapeutic apheresis can pull microplastic-consistent material from plasma, the first plausible removal pathway, but it is unvalidated and not a treatment.</>,
          <>Nanoplastics (under 1 μm) cross the blood-brain barrier; larger microplastics generally do not, size is the critical factor.</>,
          <>Where reduction helps most: filtered water in glass, no microwaving plastic, HEPA filtration for indoor air, fewer synthetic textiles.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Plastic Has Reached the Human Brain -- and the Amounts Are Rising
      </h2>
      <p>
        For most of the plastic pollution era, the brain was considered one of the body&apos;s safest
        organs. Protected by the blood-brain barrier -- a highly selective membrane that blocks most
        foreign substances from entering neural tissue -- the brain seemed insulated from the
        microplastic contamination that researchers were finding in the gut, blood, lungs, and liver.
        That assumption has been dismantled by a run of studies published between 2024 and 2026 that
        reveal not only that microplastics are present in the human brain, but that their concentrations
        are climbing -- and, as of 2026, that this holds in living people and healthy tissue, not just
        in autopsy samples.
      </p>
      <p>
        The findings have sent shockwaves through the scientific and medical communities. Plastic
        particles have now been found in every human brain sample tested. The concentrations are
        increasing over time. And preliminary data suggests a striking connection between brain plastic
        levels and neurodegenerative diseases like dementia. In this article, we examine the landmark
        research that has brought microplastics in the brain to the forefront of public health concern,
        explore the biological mechanisms that may link plastic contamination to neurological damage, and
        outline practical steps you can take to reduce your brain&apos;s exposure to these invisible
        pollutants.
      </p>

      {/* The UNM Study */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Landmark University of New Mexico Study
      </h2>
      <p>
        The study that fundamentally changed our understanding of microplastics and the brain was led
        by Dr. Matthew Campen, a professor of pharmaceutical sciences at the University of New Mexico
        (UNM). Published in 2024 and expanded upon through 2025 with additional data, the research
        examined brain tissue samples from autopsies and found microplastic particles in{' '}
        <strong>every single brain sample tested</strong>. Not some samples. Not a majority. Every one.
      </p>
      <p>
        Dr. Campen&apos;s team used pyrolysis-gas chromatography/mass spectrometry to identify and
        quantify the plastic polymers present in human brain tissue. The dominant polymer detected was
        polyethylene (PE) -- the same plastic used to make grocery bags, food packaging, plastic wrap,
        and countless other everyday items. Polypropylene, polystyrene, and polyethylene terephthalate
        (PET) were also found, though in lower concentrations.
      </p>
      <p>
        Perhaps the most disturbing finding was the temporal trend. When the UNM researchers compared
        brain samples collected in 2024 to archived brain tissue samples from 2016, they found that
        the concentration of plastic in human brain tissue had{' '}
        <strong>increased by approximately 50% over that eight-year period</strong>. This is an
        extraordinary rate of accumulation. It suggests that as global plastic production continues
        to grow -- now exceeding 400 million metric tons per year -- our bodies, and specifically our
        brains, are absorbing ever-greater quantities of plastic particles.
      </p>
      <p>
        Dr. Campen noted in interviews following the study&apos;s publication that the brain contained
        significantly higher concentrations of microplastics than other organs tested, including the
        liver and kidneys. This was unexpected, given the supposed protection of the blood-brain
        barrier, and it raised immediate questions about why plastic seems to accumulate preferentially
        in neural tissue.
      </p>

      {/* 2026 update */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What Changed in 2026: Living Patients, Healthy Brains, and a Removal Pathway
      </h2>
      <p>
        Every headline finding above came from <em>decedent</em> tissue -- brains examined after
        death. That was the single biggest limitation of the UNM work, and two papers published in
        2026 have now addressed it directly.
      </p>
      <p>
        In April 2026, Runting Li and colleagues published the first large analysis of brain tissue
        from <strong>living</strong> people in <em>Nature Health</em>. The team examined 191 brain
        samples: 156 taken from 113 living patients during tumor surgery, plus 35 from five deceased
        donors with healthy brains. Microplastics and nanoplastics turned up in{' '}
        <strong>100% of the healthy samples</strong> and 99.4% of the diseased ones. Healthy brain
        and spinal-cord tissue carried a median of <strong>50.3 micrograms per gram</strong>, while
        tumor-affected tissue peaked at <strong>129 micrograms per gram</strong>, with the highest
        readings clustered near the tumors themselves. The polymers identified were PET (drink
        bottles), polyethylene (bags and film), polyamide (textiles), and PVC (plumbing).
      </p>
      <p>
        Two things about that study deserve emphasis, because the coverage frequently got them
        wrong. First, the correlation between microplastic surface area and faster tumor growth{' '}
        <strong>does not establish causation</strong> -- the authors were explicit that the direction
        of the relationship is unresolved. Second, the same authors noted that the actual measured
        quantities are <em>lower</em> than figures circulated by EPA and HHS in April 2026. This is a
        field where the measurement methods are still being argued over, and the honest read is that
        the presence is settled while the dose is not.
      </p>
      <p>
        The second 2026 development is a Perspective by Julio Licinio and co-authors, published 5 May
        2026 in the inaugural issue of <em>Brain Health</em>. Drawing together the UNM, Marfella
        cardiovascular, and Kopatz blood-brain-barrier work, it put a number on the organ preference
        that Dr. Campen had described only qualitatively: the brain accumulates microplastic
        concentrations <strong>seven to thirty times higher than liver or kidney</strong> across the
        2016--2024 cohort, with donors diagnosed with dementia again carrying the heaviest loads. The
        authors went as far as calling brain microplastic contamination a medical emergency.
      </p>
      <p>
        That Perspective also contains the first genuinely hopeful finding in this literature.{' '}
        <strong>Therapeutic apheresis</strong> -- filtering the blood plasma, a procedure already
        used clinically for other conditions -- can extract material consistent with microplastic
        particles from human plasma. The authors describe it as the most promising candidate
        intervention the field has produced. They also supply the necessary caveat: without validated,
        reproducible, polymer-specific quantification, no removal strategy can be confirmed in the
        strict sense. Nobody should be booking apheresis to de-plastic their brain on the strength of
        this. It is a research direction, not a treatment.
      </p>

      {/* How Microplastics Cross the Blood-Brain Barrier */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Microplastics Cross the Blood-Brain Barrier
      </h2>
      <p>
        The blood-brain barrier (BBB) is one of the most selective biological membranes in the human
        body. It is formed by tightly packed endothelial cells lining the brain&apos;s blood vessels,
        supported by astrocytes and pericytes, and it serves as a gatekeeper that prevents most
        molecules, pathogens, and particles from entering the brain. Historically, the BBB was
        considered an effective shield against particulate contaminants. So how are microplastics
        getting through?
      </p>
      <p>
        The answer lies primarily in particle size. While larger microplastics -- those in the
        micrometer range -- are indeed too big to cross the intact BBB, <strong>nanoplastics</strong>,
        defined as plastic particles smaller than one micrometer (1,000 nanometers), are small enough
        to penetrate the barrier. Research published in 2024 in <em>Nanomaterials</em> demonstrated
        that polystyrene nanoparticles as small as 50 to 100 nanometers can cross the BBB through
        transcytosis, a process in which particles are absorbed by one side of a cell, transported
        through the cell interior, and released on the other side.
      </p>
      <p>
        Once nanoplastics cross the BBB, they enter brain tissue where they face an environment with
        very limited clearance mechanisms. Unlike the liver, which has specialized cells for
        processing and exporting foreign material, and unlike the gut, which can expel particles
        through the digestive tract, the brain lacks efficient pathways for removing non-biodegradable
        particles. The glymphatic system -- the brain&apos;s waste-clearance network that is most
        active during sleep -- can remove some soluble waste products, but it is not designed to
        handle solid plastic particles. This means that once nanoplastics enter the brain, they may
        accumulate over a lifetime with very little being removed.
      </p>
      <p>
        There are additional entry routes. Some researchers have proposed that nanoplastics may reach
        the brain through the olfactory nerve, bypassing the BBB entirely. Particles inhaled through
        the nose can potentially travel along olfactory nerve fibers directly into the olfactory bulb
        and from there into other brain regions. This inhalation-to-brain pathway has been demonstrated
        for other nanoparticles including ultrafine air pollution particles, and it may represent a
        significant route for airborne nanoplastics as well.
      </p>

      {/* The Dementia Connection */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Dementia Connection: 10 Times More Plastic in Affected Brains
      </h2>
      <p>
        Among the most striking and concerning findings from the UNM research was the comparison
        between brain tissue from dementia patients and age-matched controls without dementia.
        Dr. Campen&apos;s team reported that brain samples from individuals who had been diagnosed
        with dementia contained up to <strong>10 times more microplastic</strong> than samples from
        people of similar age who did not have dementia at the time of death.
      </p>
      <p>
        This tenfold difference is enormous by any biomedical standard. To put it in context, most
        biomarker differences between diseased and healthy tissue are measured in percentages or
        single-digit multiples. A tenfold difference signals either that microplastics play a
        significant role in the disease process, or that the disease process itself dramatically
        accelerates microplastic accumulation in the brain -- or possibly both.
      </p>
      <p>
        Dr. Campen and his colleagues have been careful to emphasize that this finding does not yet
        prove causation. It is possible that dementia-related deterioration of the blood-brain barrier
        allows more plastic to enter the brain passively, rather than the plastic itself driving the
        dementia. It is also possible that both the dementia and the elevated plastic levels share a
        common underlying cause. However, the magnitude of the difference has galvanized the research
        community, and multiple teams worldwide are now designing studies to determine whether the
        relationship is causal.
      </p>
      <p>
        What makes the finding particularly urgent is the global trajectory of dementia. The World
        Health Organization estimates that approximately 55 million people worldwide currently live
        with dementia, and that number is projected to reach 139 million by 2050. If microplastic
        exposure is even a contributing factor in some fraction of these cases, the public health
        implications are staggering, because unlike genetic risk factors, plastic exposure is
        modifiable.
      </p>

      {/* Mechanisms of Neurological Damage */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Microplastics May Damage the Brain: Three Key Mechanisms
      </h2>
      <p>
        Researchers have identified several biological mechanisms through which micro- and nanoplastics
        could cause or contribute to neurological damage. While the evidence varies in maturity for
        each pathway, together they paint a coherent picture of how plastic contamination could harm
        neural function.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">1. Neuroinflammation</h3>
      <p>
        When foreign particles enter the brain, the brain&apos;s resident immune cells -- microglia
        and astrocytes -- mount an inflammatory response. Microglia attempt to engulf and destroy the
        plastic particles through phagocytosis, but because plastic is not biodegradable, this process
        fails. The result is a state of chronic, unresolved neuroinflammation in which microglia remain
        perpetually activated, releasing pro-inflammatory cytokines such as TNF-alpha, interleukin-1
        beta, and interleukin-6.
      </p>
      <p>
        Chronic neuroinflammation is one of the most well-established drivers of neurodegenerative
        disease. It has been directly implicated in the pathology of Alzheimer&apos;s disease,
        Parkinson&apos;s disease, amyotrophic lateral sclerosis (ALS), and multiple sclerosis.
        Studies using animal models have confirmed that nanoplastic exposure triggers sustained
        microglial activation in the hippocampus and cortex -- the brain regions most affected by
        Alzheimer&apos;s disease -- leading to measurable cognitive decline in exposed animals.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">2. Protein Misfolding</h3>
      <p>
        A 2023 study published in <em>Science Advances</em> by researchers at Duke University
        provided one of the most mechanistically detailed findings to date. The team demonstrated that
        polystyrene nanoplastics can directly interact with brain proteins, altering their folding
        behavior and promoting the formation of abnormal protein aggregates. Specifically, they showed
        that nanoplastics accelerated the aggregation of alpha-synuclein, the protein whose misfolding
        and clumping is the hallmark of Parkinson&apos;s disease, and of amyloid-beta, the protein
        that forms the toxic plaques characteristic of Alzheimer&apos;s disease.
      </p>
      <p>
        Protein misfolding is at the core of virtually all major neurodegenerative diseases. In a
        healthy brain, proteins fold into specific three-dimensional shapes that allow them to function
        properly. When proteins misfold, they can aggregate into insoluble clumps that disrupt cellular
        function, damage neurons, and trigger further inflammatory responses. The Duke study showed
        that nanoplastic surfaces can act as nucleation sites -- essentially providing a physical
        scaffold on which misfolded proteins begin to accumulate, potentially seeding the very
        aggregates that drive neurodegeneration.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">3. Oxidative Stress</h3>
      <p>
        Nanoplastics have been shown to promote the generation of reactive oxygen species (ROS) in
        brain tissue. ROS are highly reactive molecules that, at elevated levels, damage DNA, proteins,
        and lipid membranes -- a condition known as oxidative stress. The brain is particularly
        vulnerable to oxidative stress because it has a high metabolic rate, consumes approximately
        20% of the body&apos;s oxygen supply, contains large amounts of easily oxidized fatty acids in
        cell membranes, and has relatively limited antioxidant defenses compared to other organs.
      </p>
      <p>
        Research published in 2024 in <em>Environmental Pollution</em> demonstrated that nanoplastic
        exposure in neural cell cultures led to a significant increase in ROS production, mitochondrial
        dysfunction, and ultimately apoptosis -- programmed cell death. When neurons die, they are
        generally not replaced, which means oxidative damage from nanoplastics could contribute to
        progressive, irreversible loss of brain function over time.
      </p>

      {/* Alzheimer's and Parkinson's Research */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Alzheimer&apos;s and Parkinson&apos;s Disease: What the Research Shows
      </h2>
      <p>
        The connection between microplastics and specific neurodegenerative diseases is an area of
        intense and rapidly evolving research. While definitive proof of causation remains elusive,
        the convergence of evidence from multiple independent research teams is building a compelling
        case that warrants attention. For a broader look at how microplastics affect overall health
        across all organ systems, see our comprehensive guide on{' '}
        <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
          microplastics and health effects
        </Link>.
      </p>
      <p>
        For <strong>Alzheimer&apos;s disease</strong>, the evidence centers on three converging
        findings. First, the UNM study&apos;s observation that dementia patients have dramatically
        elevated brain plastic levels. Second, the Duke University research showing that nanoplastics
        promote amyloid-beta aggregation -- the protein plaque that is the defining pathological
        feature of Alzheimer&apos;s. Third, animal studies demonstrating that chronic nanoplastic
        exposure produces hippocampal inflammation and memory deficits that mirror early Alzheimer&apos;s
        symptoms. A 2025 study in <em>Brain Research</em> found that mice chronically exposed to
        nanoplastics in drinking water developed spatial memory impairments and elevated amyloid-beta
        levels in the hippocampus after just 12 weeks of exposure.
      </p>
      <p>
        For <strong>Parkinson&apos;s disease</strong>, the evidence is similarly suggestive. The Duke
        study demonstrated that nanoplastics accelerate alpha-synuclein aggregation, and alpha-synuclein
        clumps (known as Lewy bodies) are the pathological signature of Parkinson&apos;s. Additionally,
        some of the chemical additives carried by microplastics -- particularly certain pesticides and
        heavy metals that adsorb onto plastic surfaces -- are themselves independent risk factors for
        Parkinson&apos;s disease. The possibility that microplastics serve as delivery vehicles for
        neurotoxic chemicals, concentrating them and carrying them across the blood-brain barrier,
        represents a mechanism that could help explain the rising incidence of Parkinson&apos;s disease
        worldwide, which has more than doubled over the past 25 years according to Global Burden of
        Disease data.
      </p>

      {/* What We Don't Know */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What We Don&apos;t Know Yet
      </h2>
      <p>
        It is important to be honest about the limitations of the current evidence. The science of
        microplastics in the brain is still young, and several critical questions remain unanswered.
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Causation vs. correlation:</strong> The 10x elevation in dementia patients is a
          correlation. We do not yet have prospective longitudinal studies that track microplastic
          brain levels over time and determine whether higher levels precede or follow neurological
          decline.
        </li>
        <li>
          <strong>Threshold effects:</strong> We do not know how much plastic in the brain constitutes
          a dangerous amount. Is there a safe level below which no harm occurs? Or is any accumulation
          potentially harmful? These dose-response questions are fundamental and remain unanswered.
        </li>
        <li>
          <strong>Individual variability:</strong> Factors like genetics, age, pre-existing conditions,
          BBB integrity, and overall health likely influence how susceptible any given person is to
          neurological harm from microplastics. These variables are not yet well characterized.
        </li>
        <li>
          <strong>Polymer-specific effects:</strong> Different types of plastic may have different
          neurological impacts. Polyethylene, polystyrene, and PVC carry different chemical additives
          and have different surface properties. Research has not yet systematically compared the
          neurological effects of different polymer types.
        </li>
        <li>
          <strong>Reversibility:</strong> We do not know whether reducing microplastic exposure can
          halt or reverse brain accumulation. The brain&apos;s limited clearance mechanisms suggest that
          accumulated plastic may be difficult to remove, making prevention all the more important.
        </li>
      </ul>

      {/* Reducing Brain Exposure */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to Reduce Your Brain&apos;s Exposure to Microplastics
      </h2>
      <p>
        While the science continues to develop, the precautionary principle strongly favors taking
        steps to minimize microplastic exposure, especially through the two primary routes that are
        most likely to deliver nanoplastics to the brain: inhalation and ingestion. For a complete,
        practical guide covering all aspects of microplastic avoidance, see our detailed article on{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:underline font-medium">
          how to avoid microplastics
        </Link>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Prioritize Air Quality</h3>
      <p>
        Inhalation may be the most direct route for nanoplastics to reach the brain, particularly
        through the olfactory nerve pathway that bypasses the blood-brain barrier entirely. Reducing
        airborne microplastic exposure should be a top priority.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Use a HEPA air purifier in your home, especially in bedrooms where you spend 7 to 8 hours
          breathing. HEPA filters capture particles as small as 0.3 micrometers, which includes many
          microplastic fibers and fragments.</li>
        <li>Vacuum regularly with a HEPA-filtered vacuum rather than sweeping, which can resuspend
          plastic-laden dust into the air.</li>
        <li>Reduce synthetic textiles in your home. Polyester carpets, fleece blankets, and synthetic
          curtains continuously shed microfibers into indoor air. Where possible, choose natural-fiber
          alternatives like cotton, wool, or linen.</li>
        <li>Ventilate your home regularly by opening windows when outdoor air quality permits,
          especially after activities like laundry folding or vacuuming that disturb settled fibers.</li>
        <li>Avoid burning synthetic materials, scented candles in plastic containers, and aerosol
          products that may contain microplastic propellants.</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Reduce Ingestion of Nanoplastics</h3>
      <p>
        While the gut-to-brain pathway is longer than the inhalation route, nanoplastics ingested
        through food and water can cross the intestinal barrier, enter the bloodstream, and eventually
        reach the brain via the compromised or permeable BBB.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Filter your drinking water with a reverse osmosis system, which removes particles down to
          0.0001 micrometers, effectively eliminating virtually all nano- and microplastics. Activated
          carbon filters also provide significant reduction.</li>
        <li>Avoid drinking from plastic bottles, especially those that have been exposed to heat or
          sunlight. A single liter of bottled water can contain over 240,000 nanoplastic particles
          according to a 2024 Columbia University study.</li>
        <li>Never microwave food in plastic containers or with plastic wrap. Heat dramatically
          accelerates the release of nano- and microplastics into food.</li>
        <li>Minimize consumption of ultra-processed foods, which undergo extensive contact with
          plastic machinery and packaging during manufacturing.</li>
        <li>Store food in glass, stainless steel, or ceramic containers instead of plastic.</li>
        <li>Use loose-leaf tea instead of tea bags, many of which are made from nylon or PET plastic
          and can release billions of nanoplastic particles per cup when steeped in hot water.</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Additional Protective Measures</h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>Prioritize sleep. The brain&apos;s glymphatic waste-clearance system is most active during
          deep sleep. While its ability to clear plastic particles is unproven, maintaining healthy
          glymphatic function through quality sleep is one of the few waste-clearance mechanisms the
          brain has.</li>
        <li>Exercise regularly. Physical activity improves cerebral blood flow and may support the
          brain&apos;s ability to manage inflammatory responses triggered by foreign particles.</li>
        <li>Eat antioxidant-rich foods. Given that oxidative stress is one of the primary mechanisms
          of nanoplastic brain damage, a diet rich in antioxidants -- including berries, leafy greens,
          nuts, and green tea -- may help mitigate some of the oxidative effects.</li>
        <li>Reduce plastic contact with hot foods and beverages. Heat is the single biggest
          accelerator of nanoplastic release from plastic materials.</li>
      </ul>

      {/* How the MicroPlastics App Helps */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How the MicroPlastics App Helps You Minimize Exposure
      </h2>
      <p>
        Reducing microplastic exposure effectively requires knowing which products in your daily life
        are the biggest sources of contamination. This is where the <strong>MicroPlastics app</strong>{' '}
        provides practical, actionable value. By scanning product barcodes or searching by name, you
        can instantly assess the microplastic contamination risk of foods, beverages, personal care
        products, and household items before you buy or use them.
      </p>
      <p>
        The app analyzes packaging materials, ingredient lists, and product categories to generate a
        clear risk score that helps you make informed decisions at the point of purchase. Over time,
        the app&apos;s tracking features help you identify patterns in your exposure -- which product
        categories contribute the most, which brands consistently score better, and how your overall
        exposure changes as you make substitutions.
      </p>
      <p>
        For brain health specifically, the app is particularly useful for identifying hidden sources
        of nanoplastic exposure in food packaging and beverages -- the ingestion pathway that
        research suggests is a significant contributor to systemic and brain-level contamination. By
        systematically replacing high-risk products with safer alternatives, you can meaningfully
        reduce the total volume of nano- and microplastics entering your body and potentially reaching
        your brain.
      </p>

      {/* Conclusion */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bottom Line
      </h2>
      <p>
        The discovery of microplastics in every human brain sample tested, the 50% increase in brain
        plastic concentrations over just eight years, and the tenfold elevation in dementia patients
        represent some of the most consequential findings in environmental health research in recent
        years. The work of Dr. Matthew Campen at UNM, combined with mechanistic research from Duke
        University and others, has established a credible biological framework through which
        nanoplastics could contribute to neuroinflammation, protein misfolding, oxidative stress,
        and ultimately neurodegenerative disease.
      </p>
      <p>
        We are not yet at the point where science can definitively say that microplastics cause
        Alzheimer&apos;s, Parkinson&apos;s, or other forms of dementia. The research is still
        building, and the critical longitudinal studies that will answer the causation question are
        underway but years from completion. However, the strength and consistency of the evidence
        already available, combined with the rapid increase in brain plastic levels, makes a
        compelling case for taking precautionary action now.
      </p>
      <p>
        The brain is the organ we can least afford to compromise. Unlike the liver, it
        cannot regenerate. Unlike the gut, it cannot easily expel contaminants. And unlike the lungs
        or skin, it controls everything we think, feel, remember, and do. Protecting it from
        unnecessary plastic contamination through better air quality, cleaner water, smarter food
        choices, and informed product selection is not an overreaction to uncertain science -- it is a
        reasonable response to a rapidly growing body of evidence that demands our attention.
      </p>
      <p>
        Start by making the changes that have the highest impact: filter your drinking water, reduce
        airborne plastic fibers in your home, stop heating food in plastic, and use the MicroPlastics
        app to identify and replace the products that contribute the most to your daily exposure. These
        are practical, achievable steps that can meaningfully reduce the amount of plastic reaching
        your brain -- and every step counts.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Product packaging. PET, HDPE, PP, PS, PVC, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, flags for known PFAS / BPA / fragranced lines.</>,
          <>Use-context flags, heat exposure, microwave, reuse cycles.</>,
          <>Cited research, every score links the specific studies behind it.</>,
        ]}
      />

      <BlogAppCTA
        headline="Translate the research into 5-second shelf decisions"
        sub="Reading the studies is step one. Acting on them at the grocery store is step two. The MicroPlastics app scores each product 0–100 using research like this."
        buttonLabel="Get the MicroPlastics app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Are microplastics found in the human brain?',
            answer:
              'Yes. A 2025 University of New Mexico study (Nihart et al.) detected microplastics in every brain sample analyzed, with concentrations roughly 50% higher in 2024 vs 2016. Polyethylene was the dominant polymer. The frontal cortex was the primary tissue examined.',
          },
          {
            question: 'Do dementia patients have more microplastics in their brain?',
            answer:
              'The UNM 2025 study found patients diagnosed with dementia had roughly 10× more microplastic in their brain tissue than non-dementia controls. The study cannot prove causation, neurodegeneration could allow particles to accumulate, or accumulated particles could contribute to neurodegeneration. Both directions are plausible.',
          },
          {
            question: 'How do microplastics get into the brain?',
            answer:
              'Nanoplastics (particles smaller than 1 μm) can cross the blood-brain barrier directly from circulation. Larger microplastics generally cannot. Inhaled particles can also travel via the olfactory nerve directly into brain tissue, bypassing the blood-brain barrier entirely.',
          },
          {
            question: 'Can microplastics in the brain be removed?',
            answer:
              'No proven protocol exists to remove microplastics already in brain tissue. The barrier that protects the brain also makes it slow to clear particles. Current research focuses on reducing intake rather than elimination.',
          },
          {
            question: 'What is the single best action to reduce brain exposure to microplastics?',
            answer:
              'Two changes account for most of the reduction available to a typical adult: switch from bottled to filtered water in a glass or stainless container, and stop microwaving food in plastic. Both attack the highest-flux exposure routes.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Li R, et al.',
            year: 2026,
            title: 'Microplastics and nanoplastics in human brain tissue from living patients and healthy donors',
            publisher: 'Nature Health',
            url: 'https://doi.org/10.1038/s44360-026-00091-4',
          },
          {
            authors: 'Licinio J, Bornstein SR, Steenblock C, Fabiano N, et al.',
            year: 2026,
            title: 'The human microplastic burden and brain health: from measurement to pathophysiology and removal',
            publisher: 'Brain Health',
            url: 'https://doi.org/10.61373/bh026p.0006',
          },
          {
            authors: 'Nihart AJ, Garcia MA, El Hayek E, et al.',
            year: 2025,
            title: 'Bioaccumulation of microplastics in decedent human brains',
            publisher: 'Nature Medicine',
            url: 'https://www.nature.com/articles/s41591-024-03453-1',
          },
          {
            authors: 'Marfella R, Prattichizzo F, Sardu C, et al.',
            year: 2024,
            title: 'Microplastics and Nanoplastics in Atheromas and Cardiovascular Events',
            publisher: 'New England Journal of Medicine',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822',
          },
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
          {
            authors: 'Jenner LC, Rotchell JM, Bennett RT, et al.',
            year: 2022,
            title: 'Detection of microplastics in human lung tissue using FTIR spectroscopy',
            publisher: 'Science of the Total Environment',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0048969722020009',
          },
          {
            authors: 'Garcia MA, Liu R, Nihart A, et al.',
            year: 2024,
            title: 'Quantitation and identification of microplastics in human placental specimens',
            publisher: 'Toxicological Sciences',
            url: 'https://academic.oup.com/toxsci/article/199/1/81/7609299',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles and potential implications for human health',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
